import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { questionnaireContent } from '../questionnaire.content';
import { Commands, Questionnaire } from '@local/client-contracts';
import { CommandsService } from 'src/app/bar/services/commands/commands.service';
import { BlobsService } from 'src/app/bar/services/blobs/blob.service';
import { ShowToasterService } from 'src/app/bar/services/show-toaster.service';
import { DownloadsService } from '@shared/services/downloads.service';
import { isEmbed, isMac, isNativeWindow } from '@local/common-web';
import { cutText } from '@local/ts-infra';
import { Logger } from '@unleash-tech/js-logger';
import { LogService } from '@shared/services';
import { QuestionnaireData } from '../questionnaire.component';

@UntilDestroy()
@Component({
  selector: 'summary-questionnaire',
  templateUrl: './summary-questionnaire.component.html',
  styleUrls: ['./summary-questionnaire.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SummaryQuestionnaireComponent {
  private readonly DOWNLOAD_ID = 'download-id';
  readonly questionnaireContent = questionnaireContent;
  private logger: Logger;
  private _data: QuestionnaireData;

  summaryWorksheets: Questionnaire.WorksheetResponse[];
  answeredTotal: number;
  noAnsweredTotal: number;
  failedTotal: number;
  questionsTotal: number;
  blobId: string;

  constructor(
    private cdr: ChangeDetectorRef,
    private commandsService: CommandsService,
    private blobsService: BlobsService,
    private showToasterService: ShowToasterService,
    private downloadsService: DownloadsService,
    private logService: LogService
  ) {
    this.logger = this.logService.scope('SummaryQuestionnaire');
  }

  @Input() set data(val: QuestionnaireData) {
    this._data = val;
    this.setData(this.data.activeProcess);
  }

  get data(): QuestionnaireData {
    return this._data;
  }

  @Output() onDownload = new EventEmitter<void>();

  async downloadFile() {
    const blobUrl = await this.blobsService.getDownloadUrl(this.blobId);
    const command: Commands.DownloadUrl = {
      type: 'download-url',
      grantType: 'blobs',
      url: blobUrl,
      name: this.data.fileName,
    };

    this.commandsService
      .executeCommand(command)
      .then(() => {
        this.onDownload.emit();
        this.showDownloadToaster();
      })
      .catch((error) => {
        this.logger.error('failed to download rfp file', error);
        this.showToasterService.showErrorToaster(this.DOWNLOAD_ID);
      });
  }

  private async showInFolder(): Promise<void> {
    const values = Object.values(this.downloadsService.all);
    if (!values?.length) {
      return;
    }
    const { path } = values[0];
    const command: Commands.OpenUrl = { type: 'open-url', url: path };
    await this.commandsService.executeCommand(command, {});
  }

  private setData(summary: Questionnaire.QuestionnaireStatusResponse) {
    this.summaryWorksheets = summary.worksheets;
    this.answeredTotal = this.summaryWorksheets.reduce((sum, sheet) => sum + sheet.answered, 0);
    this.noAnsweredTotal = this.summaryWorksheets.reduce((sum, sheet) => sum + sheet.notAnswered, 0);
    this.failedTotal = this.summaryWorksheets.reduce((sum, sheet) => sum + sheet.failed, 0);
    this.questionsTotal = summary.completed;
    this.blobId = summary.resultBlobId;
    this.cdr.markForCheck();
  }

  private showDownloadToaster() {
    if (!isNativeWindow()) {
      return;
    }
    setTimeout(() => {
      const toaster = this.showToasterService.showToaster({
        id: this.DOWNLOAD_ID,
        intent: 'primary',
        timeout: 10000,
        title: `${questionnaireContent.requestForProposal} ${cutText(this.data.fileName, 20) || ''}.${this.data.extension}`,
        icon: { type: 'img', value: { lightUrl: '/assets/file-icons/xlsx.svg' } },
        buttonText: `Show in ${isMac() ? 'Finder' : 'Folder'}`,
      });
      toaster.compInstance.invoke.subscribe(() => {
        this.showInFolder();
        toaster.destroy();
      });
    }, 500);
  }
}
